// Translation system for e-health landing page
// Languages: EN, FR, DE, LV, LT, EE

export const translations = {
  en: {
    // Header
    nav: {
      solutions: 'Solutions',
      devices: 'Devices',
      pilot: 'Pilot Program',
      contact: 'Contact'
    },
    
    // Hero
    hero: {
      title: 'Smart Health Ecosystem for',
      titleHighlight: 'Elder Care & Hospitals',
      benefits: ['Reduce Costs', 'Improve Safety', 'Support Staff'],
      description: 'Real-time monitoring, patient tracking, emergency alerts, environment sensors and caregiver tools — all in one unified EU-hosted platform. Apps available in LV, LT, EE, EN.',
      ctaPrimary: 'Start a Pilot',
      ctaSecondary: 'Explore Solutions'
    },
    
    // Challenges
    challenges: {
      title: 'Healthcare Challenges',
      subtitle: 'Modern healthcare faces critical obstacles that demand innovative solutions',
      items: [
        {
          title: 'Rising Operational Costs',
          description: 'Staff lose time searching equipment, performing unnecessary visits and managing manual logs.'
        },
        {
          title: 'Lack of Real-Time Visibility',
          description: 'Late detection of risks, wandering patients, unseen seniors for hours.'
        },
        {
          title: 'Staff Overload',
          description: 'Too many tasks, too few people. E2 automates and simplifies.'
        }
      ]
    },
    
    // Solutions
    solutions: {
      title: 'The E2 Healthcare Ecosystem',
      subtitle: 'Four integrated solutions designed to transform elderly care and hospital operations',
      learnMore: 'Learn more',
      items: [
        {
          title: 'Elderly Safety & Home Monitoring',
          description: 'Fall detection, GPS safety, behaviour insights, family alerts, Alexa voice help.'
        },
        {
          title: 'Hospital Patient & Equipment Tracking',
          description: 'Track patients, beds, wheelchairs and pumps in real time.'
        },
        {
          title: 'Environment & Comfort Monitoring',
          description: 'CO₂, temperature, humidity, sleep patterns.'
        },
        {
          title: 'Caregiver & Medical Platform (eDoctor)',
          description: 'Alerts, notes, trends, follow-up tools — available in LV, LT, EE.'
        }
      ]
    },
    
    // Devices
    devices: {
      title: 'Our Devices',
      subtitle: 'Medical-Grade, Reliable, Simple',
      items: [
        'Asset Tracker',
        'Smart Badge',
        'Signal Repeater',
        'Patient Wristband',
        'Air Quality Sensor',
        'Panic Button',
        'Indoor Patient Locator',
        'Smart Energy Counter',
        'Other Sensors'
      ]
    },
    
    // Outcomes
    outcomes: {
      title: 'Outcomes & ROI',
      subtitle: 'Measurable impact on costs, care quality, and operational efficiency',
      items: [
        {
          title: 'Reduce Costs',
          bullets: [
            'Fewer unnecessary visits',
            'Fewer equipment losses',
            'Automated logs',
            'Fewer emergency incidents'
          ]
        },
        {
          title: 'Improve Quality of Care',
          bullets: [
            'Real-time alerts',
            'Early risk detection',
            'Transparency for families',
            'Better comfort'
          ]
        },
        {
          title: 'Increase Staff Efficiency',
          bullets: [
            'Unified dashboards',
            'WhatsApp alerts',
            'Voice assistance (Alexa local)'
          ]
        }
      ]
    },
    
    // How It Works
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Simple deployment, immediate impact',
      steps: [
        'Install sensors & wearables in minutes',
        'Seniors or patients use the watch or badge',
        'Data flows into eDoctor dashboard',
        'Alerts via WhatsApp or Alexa',
        'Care teams act immediately'
      ],
      note: 'No IT integration required.'
    },
    
    // Pilot Program
    pilot: {
      title: 'Start Your 30-Day Pilot',
      subtitle: 'Choose your pilot focus and see immediate results',
      cards: [
        {
          title: 'Elder Safety & Home Monitoring',
          description: 'GPS Watch, eBeat, PIR, family alerts.',
          button: 'Start Elder Pilot'
        },
        {
          title: 'Hospital Tracking',
          description: 'Patient locator, bed tracking, asset tracking.',
          button: 'Start Hospital Pilot'
        },
        {
          title: 'Environment Monitoring',
          description: 'CO₂, humidity, temperature, sleep insights.',
          button: 'Start Environment Pilot'
        },
        {
          title: 'Full Ecosystem',
          description: 'All devices + dashboards + apps.',
          button: 'Start Full Pilot'
        }
      ],
      ctaPrimary: 'Start Pilot Now',
      ctaSecondary: 'Book a 10-minute Call'
    },
    
    // Contact Form
    contact: {
      title: 'Get In Touch',
      subtitle: 'Tell us your needs — we respond within 24 hours',
      gdprNote: 'EU-hosted & GDPR-compliant',
      gdprDescription: 'Your data is secure and fully compliant with European privacy regulations.',
      form: {
        fullName: 'Full Name',
        email: 'Email',
        organisation: 'Organisation',
        country: 'Country',
        phone: 'Phone (optional)',
        interests: 'Areas of Interest',
        objectives: 'Objectives',
        message: 'Message (optional)',
        messagePlaceholder: 'Tell us more about your needs...',
        submit: 'Send Request',
        sending: 'Sending...'
      },
      interestOptions: [
        'Elder Monitoring',
        'Patient Tracking',
        'Equipment Tracking',
        'Environment Monitoring',
        'Wearables',
        'Communication Tools',
        'Full Ecosystem'
      ],
      objectiveOptions: [
        'Reduce costs',
        'Improve care quality',
        'Increase safety',
        'Increase staff efficiency'
      ]
    },
    
    // Funding
    funding: {
      title: 'Funding & Financial Support',
      subtitle: 'for Hospitals and Municipalities',
      description: 'We help you secure the budget you need.',
      cards: [
        {
          title: 'Flexible Financing',
          items: [
            'OPEX monthly plans',
            'Equipment leasing (3–5 years)',
            'No upfront CAPEX',
            'Easy to approve for hospitals and municipalities'
          ]
        },
        {
          title: 'EU & National Funding Compatibility',
          items: [
            'Digital health',
            'Elder care / aging population',
            'Smart City initiatives',
            'Social care grants',
            'ERDF / ESF+ / national programs'
          ],
          note: 'We can provide a justification pack to support your funding request.'
        },
        {
          title: 'Pilot as a Budget Unlock Tool',
          items: [
            '30-day pilot',
            'Real data and ROI',
            'Helps justify internal approval',
            'No commitment'
          ]
        }
      ]
    },
    
    // FAQ
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about our healthcare ecosystem',
      items: [
        {
          question: 'Is the platform GDPR compliant?',
          answer: 'Yes — EU-hosted, privacy-by-design.'
        },
        {
          question: 'Are the apps available in LV, LT, EE?',
          answer: 'Yes — full localisation for seniors, caregivers and hospitals.'
        },
        {
          question: 'How fast can deployment happen?',
          answer: '24–48 hours depending on scope.'
        },
        {
          question: 'Do you support both elderly at home and hospitals?',
          answer: 'Yes — one ecosystem for both.'
        },
        {
          question: 'Are WhatsApp alerts supported?',
          answer: 'Yes — they go directly to caregivers and family.'
        },
        {
          question: 'Is the pilot contract-free?',
          answer: 'Yes — no commitment.'
        }
      ]
    },
    
    // Footer
    footer: {
      countries: {
        lithuania: 'Lithuania',
        latvia: 'Latvia',
        estonia: 'Estonia'
      },
      copyright: 'All rights reserved.'
    }
  },
  
  fr: {
    // Header
    nav: {
      solutions: 'Solutions',
      devices: 'Appareils',
      pilot: 'Programme Pilote',
      contact: 'Contact'
    },
    
    // Hero
    hero: {
      title: 'Écosystème de Santé Intelligent pour',
      titleHighlight: 'Soins aux Personnes Âgées et Hôpitaux',
      benefits: ['Réduire les Coûts', 'Améliorer la Sécurité', 'Soutenir le Personnel'],
      description: 'Surveillance en temps réel, suivi des patients, alertes d\'urgence, capteurs environnementaux et outils pour soignants — le tout sur une plateforme unifiée hébergée dans l\'UE. Applications disponibles en LV, LT, EE, EN.',
      ctaPrimary: 'Démarrer un Pilote',
      ctaSecondary: 'Découvrir les Solutions'
    },
    
    // Challenges
    challenges: {
      title: 'Défis de la Santé',
      subtitle: 'Le secteur de la santé moderne fait face à des obstacles critiques qui nécessitent des solutions innovantes',
      items: [
        {
          title: 'Coûts Opérationnels Croissants',
          description: 'Le personnel perd du temps à chercher du matériel, effectuer des visites inutiles et gérer des registres manuels.'
        },
        {
          title: 'Manque de Visibilité en Temps Réel',
          description: 'Détection tardive des risques, patients errants, personnes âgées non surveillées pendant des heures.'
        },
        {
          title: 'Surcharge du Personnel',
          description: 'Trop de tâches, trop peu de personnes. E2 automatise et simplifie.'
        }
      ]
    },
    
    // Solutions
    solutions: {
      title: 'L\'Écosystème de Santé E2',
      subtitle: 'Quatre solutions intégrées conçues pour transformer les soins aux personnes âgées et les opérations hospitalières',
      learnMore: 'En savoir plus',
      items: [
        {
          title: 'Sécurité des Personnes Âgées et Surveillance à Domicile',
          description: 'Détection de chute, sécurité GPS, analyses comportementales, alertes familiales, assistance vocale Alexa.'
        },
        {
          title: 'Suivi des Patients et Équipements Hospitaliers',
          description: 'Suivi en temps réel des patients, lits, fauteuils roulants et pompes.'
        },
        {
          title: 'Surveillance de l\'Environnement et du Confort',
          description: 'CO₂, température, humidité, cycles de sommeil.'
        },
        {
          title: 'Plateforme pour Soignants et Médecins (eDoctor)',
          description: 'Alertes, notes, tendances, outils de suivi — disponible en LV, LT, EE.'
        }
      ]
    },
    
    // Devices
    devices: {
      title: 'Nos Appareils',
      subtitle: 'Qualité Médicale, Fiable, Simple',
      items: [
        'Traceur d\'Actifs',
        'Badge Intelligent',
        'Répéteur de Signal',
        'Bracelet Patient',
        'Capteur de Qualité d\'Air',
        'Bouton Panique',
        'Localisateur Patient Intérieur',
        'Compteur Énergétique Intelligent',
        'Autres Capteurs'
      ]
    },
    
    // Outcomes
    outcomes: {
      title: 'Résultats & ROI',
      subtitle: 'Impact mesurable sur les coûts, la qualité des soins et l\'efficacité opérationnelle',
      items: [
        {
          title: 'Réduire les Coûts',
          bullets: [
            'Moins de visites inutiles',
            'Moins de pertes d\'équipement',
            'Journaux automatisés',
            'Moins d\'incidents d\'urgence'
          ]
        },
        {
          title: 'Améliorer la Qualité des Soins',
          bullets: [
            'Alertes en temps réel',
            'Détection précoce des risques',
            'Transparence pour les familles',
            'Meilleur confort'
          ]
        },
        {
          title: 'Augmenter l\'Efficacité du Personnel',
          bullets: [
            'Tableaux de bord unifiés',
            'Alertes WhatsApp',
            'Assistance vocale (Alexa local)'
          ]
        }
      ]
    },
    
    // How It Works
    howItWorks: {
      title: 'Comment ça Fonctionne',
      subtitle: 'Déploiement simple, impact immédiat',
      steps: [
        'Installer les capteurs et wearables en quelques minutes',
        'Les seniors ou patients utilisent la montre ou le badge',
        'Les données affluent vers le tableau de bord eDoctor',
        'Alertes via WhatsApp ou Alexa',
        'Les équipes soignantes agissent immédiatement'
      ],
      note: 'Aucune intégration IT requise.'
    },
    
    // Pilot Program
    pilot: {
      title: 'Démarrez Votre Pilote de 30 Jours',
      subtitle: 'Choisissez votre focus pilote et voyez des résultats immédiats',
      cards: [
        {
          title: 'Sécurité des Personnes Âgées et Surveillance à Domicile',
          description: 'Montre GPS, eBeat, PIR, alertes familiales.',
          button: 'Démarrer Pilote Seniors'
        },
        {
          title: 'Suivi Hospitalier',
          description: 'Localisateur patient, suivi de lits, suivi d\'actifs.',
          button: 'Démarrer Pilote Hôpital'
        },
        {
          title: 'Surveillance Environnementale',
          description: 'CO₂, humidité, température, analyses du sommeil.',
          button: 'Démarrer Pilote Environnement'
        },
        {
          title: 'Écosystème Complet',
          description: 'Tous les appareils + tableaux de bord + applications.',
          button: 'Démarrer Pilote Complet'
        }
      ],
      ctaPrimary: 'Démarrer le Pilote Maintenant',
      ctaSecondary: 'Réserver un Appel de 10 Minutes'
    },
    
    // Contact Form
    contact: {
      title: 'Nous Contacter',
      subtitle: 'Dites-nous vos besoins — nous répondons sous 24 heures',
      gdprNote: 'Hébergé dans l\'UE et conforme au RGPD',
      gdprDescription: 'Vos données sont sécurisées et entièrement conformes aux réglementations européennes sur la confidentialité.',
      form: {
        fullName: 'Nom Complet',
        email: 'E-mail',
        organisation: 'Organisation',
        country: 'Pays',
        phone: 'Téléphone (optionnel)',
        interests: 'Domaines d\'Intérêt',
        objectives: 'Objectifs',
        message: 'Message (optionnel)',
        messagePlaceholder: 'Parlez-nous de vos besoins...',
        submit: 'Envoyer la Demande',
        sending: 'Envoi en cours...'
      },
      interestOptions: [
        'Surveillance des Seniors',
        'Suivi des Patients',
        'Suivi des Équipements',
        'Surveillance Environnementale',
        'Objets Connectés',
        'Outils de Communication',
        'Écosystème Complet'
      ],
      objectiveOptions: [
        'Réduire les coûts',
        'Améliorer la qualité des soins',
        'Augmenter la sécurité',
        'Augmenter l\'efficacité du personnel'
      ]
    },
    
    // Funding
    funding: {
      title: 'Financement et Soutien Budgétaire',
      subtitle: 'pour les Hôpitaux et Municipalités',
      description: 'Nous vous aidons à obtenir le budget dont vous avez besoin.',
      cards: [
        {
          title: 'Financement Flexible',
          items: [
            'Plans mensuels OPEX',
            'Leasing d\'équipement (3-5 ans)',
            'Pas de CAPEX initial',
            'Facile à approuver pour les hôpitaux et municipalités'
          ]
        },
        {
          title: 'Compatibilité avec les Financements UE et Nationaux',
          items: [
            'Santé numérique',
            'Soins aux personnes âgées / vieillissement de la population',
            'Initiatives Smart City',
            'Subventions aux soins sociaux',
            'FEDER / FSE+ / programmes nationaux'
          ],
          note: 'Nous pouvons fournir un dossier de justification pour soutenir votre demande de financement.'
        },
        {
          title: 'Le Pilote comme Outil de Déblocage Budgétaire',
          items: [
            'Pilote de 30 jours',
            'Données réelles et ROI',
            'Aide à justifier l\'approbation interne',
            'Sans engagement'
          ]
        }
      ]
    },
    
    // FAQ
    faq: {
      title: 'Questions Fréquemment Posées',
      subtitle: 'Tout ce que vous devez savoir sur notre écosystème de santé',
      items: [
        {
          question: 'La plateforme est-elle conforme au RGPD ?',
          answer: 'Oui — hébergée dans l\'UE, protection de la vie privée dès la conception.'
        },
        {
          question: 'Les applications sont-elles disponibles en LV, LT, EE ?',
          answer: 'Oui — localisation complète pour les seniors, soignants et hôpitaux.'
        },
        {
          question: 'Quelle est la rapidité du déploiement ?',
          answer: '24 à 48 heures selon la portée.'
        },
        {
          question: 'Soutenez-vous les personnes âgées à domicile et les hôpitaux ?',
          answer: 'Oui — un seul écosystème pour les deux.'
        },
        {
          question: 'Les alertes WhatsApp sont-elles prises en charge ?',
          answer: 'Oui — elles vont directement aux soignants et aux familles.'
        },
        {
          question: 'Le pilote est-il sans engagement ?',
          answer: 'Oui — aucun engagement.'
        }
      ]
    },
    
    // Footer
    footer: {
      countries: {
        lithuania: 'Lituanie',
        latvia: 'Lettonie',
        estonia: 'Estonie'
      },
      copyright: 'Tous droits réservés.'
    }
  },
  
  de: {
    // Header
    nav: {
      solutions: 'Lösungen',
      devices: 'Geräte',
      pilot: 'Pilotprogramm',
      contact: 'Kontakt'
    },
    
    // Hero
    hero: {
      title: 'Intelligentes Gesundheitsökosystem für',
      titleHighlight: 'Altenpflege & Krankenhäuser',
      benefits: ['Kosten Senken', 'Sicherheit Verbessern', 'Personal Unterstützen'],
      description: 'Echtzeit-Überwachung, Patientenverfolgung, Notfallalarme, Umgebungssensoren und Pflegewerkzeuge — alles auf einer einheitlichen EU-gehosteten Plattform. Apps verfügbar in LV, LT, EE, EN.',
      ctaPrimary: 'Pilotprojekt Starten',
      ctaSecondary: 'Lösungen Erkunden'
    },
    
    // Challenges
    challenges: {
      title: 'Herausforderungen im Gesundheitswesen',
      subtitle: 'Das moderne Gesundheitswesen steht vor kritischen Hindernissen, die innovative Lösungen erfordern',
      items: [
        {
          title: 'Steigende Betriebskosten',
          description: 'Personal verliert Zeit bei der Suche nach Geräten, bei unnötigen Besuchen und bei der manuellen Protokollierung.'
        },
        {
          title: 'Mangelnde Echtzeit-Sichtbarkeit',
          description: 'Späte Risikoerkennung, umherirrende Patienten, stundenlang unbeobachtete Senioren.'
        },
        {
          title: 'Personalüberlastung',
          description: 'Zu viele Aufgaben, zu wenig Menschen. E2 automatisiert und vereinfacht.'
        }
      ]
    },
    
    // Solutions
    solutions: {
      title: 'Das E2 Gesundheitsökosystem',
      subtitle: 'Vier integrierte Lösungen zur Transformation der Altenpflege und des Krankenhausbetriebs',
      learnMore: 'Mehr erfahren',
      items: [
        {
          title: 'Seniorensicherheit & Hausüberwachung',
          description: 'Sturzerkennung, GPS-Sicherheit, Verhaltensanalysen, Familienalarme, Alexa-Sprachhilfe.'
        },
        {
          title: 'Krankenhaus-Patienten- & Geräteverfolgung',
          description: 'Verfolgen Sie Patienten, Betten, Rollstühle und Pumpen in Echtzeit.'
        },
        {
          title: 'Umgebungs- & Komfortüberwachung',
          description: 'CO₂, Temperatur, Luftfeuchtigkeit, Schlafmuster.'
        },
        {
          title: 'Pflege- & Medizinplattform (eDoctor)',
          description: 'Alarme, Notizen, Trends, Follow-up-Tools — verfügbar in LV, LT, EE.'
        }
      ]
    },
    
    // Devices
    devices: {
      title: 'Unsere Geräte',
      subtitle: 'Medizinische Qualität, Zuverlässig, Einfach',
      items: [
        'Asset-Tracker',
        'Intelligenter Ausweis',
        'Signalverstärker',
        'Patientenarmband',
        'Luftqualitätssensor',
        'Panik-Knopf',
        'Innenraum-Patientenortung',
        'Intelligenter Energiezähler',
        'Andere Sensoren'
      ]
    },
    
    // Outcomes
    outcomes: {
      title: 'Ergebnisse & ROI',
      subtitle: 'Messbarer Einfluss auf Kosten, Pflegequalität und operative Effizienz',
      items: [
        {
          title: 'Kosten Senken',
          bullets: [
            'Weniger unnötige Besuche',
            'Weniger Geräteverluste',
            'Automatisierte Protokolle',
            'Weniger Notfälle'
          ]
        },
        {
          title: 'Pflegequalität Verbessern',
          bullets: [
            'Echtzeit-Alarme',
            'Früherkennung von Risiken',
            'Transparenz für Familien',
            'Besserer Komfort'
          ]
        },
        {
          title: 'Personaleffizienz Steigern',
          bullets: [
            'Einheitliche Dashboards',
            'WhatsApp-Alarme',
            'Sprachassistenz (Alexa lokal)'
          ]
        }
      ]
    },
    
    // How It Works
    howItWorks: {
      title: 'Wie es Funktioniert',
      subtitle: 'Einfache Bereitstellung, sofortige Wirkung',
      steps: [
        'Sensoren & Wearables in Minuten installieren',
        'Senioren oder Patienten nutzen die Uhr oder das Abzeichen',
        'Daten fließen in das eDoctor-Dashboard',
        'Alarme über WhatsApp oder Alexa',
        'Pflegeteams handeln sofort'
      ],
      note: 'Keine IT-Integration erforderlich.'
    },
    
    // Pilot Program
    pilot: {
      title: 'Starten Sie Ihr 30-Tage-Pilotprojekt',
      subtitle: 'Wählen Sie Ihren Pilotfokus und sehen Sie sofortige Ergebnisse',
      cards: [
        {
          title: 'Seniorensicherheit & Hausüberwachung',
          description: 'GPS-Uhr, eBeat, PIR, Familienalarme.',
          button: 'Senioren-Pilot Starten'
        },
        {
          title: 'Krankenhaus-Tracking',
          description: 'Patientenortung, Bettenverfolgung, Asset-Tracking.',
          button: 'Krankenhaus-Pilot Starten'
        },
        {
          title: 'Umgebungsüberwachung',
          description: 'CO₂, Luftfeuchtigkeit, Temperatur, Schlafanalysen.',
          button: 'Umgebungs-Pilot Starten'
        },
        {
          title: 'Vollständiges Ökosystem',
          description: 'Alle Geräte + Dashboards + Apps.',
          button: 'Vollständigen Pilot Starten'
        }
      ],
      ctaPrimary: 'Pilot Jetzt Starten',
      ctaSecondary: 'Einen 10-Minuten-Anruf Buchen'
    },
    
    // Contact Form
    contact: {
      title: 'Kontakt Aufnehmen',
      subtitle: 'Teilen Sie uns Ihre Bedürfnisse mit — wir antworten innerhalb von 24 Stunden',
      gdprNote: 'EU-gehostet & DSGVO-konform',
      gdprDescription: 'Ihre Daten sind sicher und vollständig konform mit europäischen Datenschutzbestimmungen.',
      form: {
        fullName: 'Vollständiger Name',
        email: 'E-Mail',
        organisation: 'Organisation',
        country: 'Land',
        phone: 'Telefon (optional)',
        interests: 'Interessenbereiche',
        objectives: 'Ziele',
        message: 'Nachricht (optional)',
        messagePlaceholder: 'Erzählen Sie uns mehr über Ihre Bedürfnisse...',
        submit: 'Anfrage Senden',
        sending: 'Wird gesendet...'
      },
      interestOptions: [
        'Seniorenüberwachung',
        'Patientenverfolgung',
        'Geräteverfolgung',
        'Umgebungsüberwachung',
        'Wearables',
        'Kommunikationswerkzeuge',
        'Vollständiges Ökosystem'
      ],
      objectiveOptions: [
        'Kosten senken',
        'Pflegequalität verbessern',
        'Sicherheit erhöhen',
        'Personaleffizienz steigern'
      ]
    },
    
    // Funding
    funding: {
      title: 'Finanzierung & Budgetunterstützung',
      subtitle: 'für Krankenhäuser und Gemeinden',
      description: 'Wir helfen Ihnen, das benötigte Budget zu sichern.',
      cards: [
        {
          title: 'Flexible Finanzierung',
          items: [
            'OPEX-Monatspläne',
            'Geräteleasing (3-5 Jahre)',
            'Kein Vorab-CAPEX',
            'Leicht zu genehmigen für Krankenhäuser und Gemeinden'
          ]
        },
        {
          title: 'EU- & Nationale Förderungskompatibilität',
          items: [
            'Digitale Gesundheit',
            'Altenpflege / alternde Bevölkerung',
            'Smart-City-Initiativen',
            'Sozialhilfezuschüsse',
            'EFRE / ESF+ / nationale Programme'
          ],
          note: 'Wir können ein Begründungspaket zur Unterstützung Ihres Förderantrags bereitstellen.'
        },
        {
          title: 'Pilot als Budget-Freischaltungswerkzeug',
          items: [
            '30-Tage-Pilot',
            'Echte Daten und ROI',
            'Hilft bei der internen Genehmigung',
            'Keine Verpflichtung'
          ]
        }
      ]
    },
    
    // FAQ
    faq: {
      title: 'Häufig Gestellte Fragen',
      subtitle: 'Alles, was Sie über unser Gesundheitsökosystem wissen müssen',
      items: [
        {
          question: 'Ist die Plattform DSGVO-konform?',
          answer: 'Ja — EU-gehostet, Datenschutz durch Design.'
        },
        {
          question: 'Sind die Apps in LV, LT, EE verfügbar?',
          answer: 'Ja — vollständige Lokalisierung für Senioren, Pflegekräfte und Krankenhäuser.'
        },
        {
          question: 'Wie schnell kann die Bereitstellung erfolgen?',
          answer: '24–48 Stunden je nach Umfang.'
        },
        {
          question: 'Unterstützen Sie sowohl Senioren zu Hause als auch Krankenhäuser?',
          answer: 'Ja — ein Ökosystem für beide.'
        },
        {
          question: 'Werden WhatsApp-Alarme unterstützt?',
          answer: 'Ja — sie gehen direkt an Pflegekräfte und Familie.'
        },
        {
          question: 'Ist der Pilot vertragsfrei?',
          answer: 'Ja — keine Verpflichtung.'
        }
      ]
    },
    
    // Footer
    footer: {
      countries: {
        lithuania: 'Litauen',
        latvia: 'Lettland',
        estonia: 'Estland'
      },
      copyright: 'Alle Rechte vorbehalten.'
    }
  },
  
  lv: {
    // Header
    nav: {
      solutions: 'Risinājumi',
      devices: 'Ierīces',
      pilot: 'Pilotprogramma',
      contact: 'Kontakti'
    },
    
    // Hero
    hero: {
      title: 'Viedā Veselības Ekosistēma',
      titleHighlight: 'Vecāka Gadagājuma Cilvēku Aprūpei un Slimnīcām',
      benefits: ['Samazināt Izmaksas', 'Uzlabot Drošību', 'Atbalstīt Personālu'],
      description: 'Reāllaika uzraudzība, pacientu izsekošana, ārkārtas brīdinājumi, vides sensori un aprūpētāju rīki — viss vienā vienotā ES platformā. Lietotnes pieejamas LV, LT, EE, EN.',
      ctaPrimary: 'Sākt Pilotu',
      ctaSecondary: 'Izpētīt Risinājumus'
    },
    
    // Challenges
    challenges: {
      title: 'Veselības Aprūpes Izaicinājumi',
      subtitle: 'Mūsdienu veselības aprūpe saskaras ar kritiskiem šķēršļiem, kas prasa inovatīvus risinājumus',
      items: [
        {
          title: 'Augošas Darbības Izmaksas',
          description: 'Personāls zaudē laiku, meklējot aprīkojumu, veicot nevajadzīgus apmeklējumus un pārvaldot manuālos žurnālus.'
        },
        {
          title: 'Reāllaika Redzamības Trūkums',
          description: 'Novēlota risku atklāšana, klīstoši pacienti, neuzraudzīti vecāka gadagājuma cilvēki stundām ilgi.'
        },
        {
          title: 'Personāla Pārslodze',
          description: 'Pārāk daudz uzdevumu, pārāk maz cilvēku. E2 automatizē un vienkāršo.'
        }
      ]
    },
    
    // Solutions
    solutions: {
      title: 'E2 Veselības Aprūpes Ekosistēma',
      subtitle: 'Četri integrēti risinājumi, kas paredzēti vecāka gadagājuma cilvēku aprūpes un slimnīcu darbību transformēšanai',
      learnMore: 'Uzzināt vairāk',
      items: [
        {
          title: 'Vecāka Gadagājuma Cilvēku Drošība un Mājas Uzraudzība',
          description: 'Kritiena noteikšana, GPS drošība, uzvedības analīze, ģimenes brīdinājumi, Alexa balss palīdzība.'
        },
        {
          title: 'Slimnīcas Pacientu un Aprīkojuma Izsekošana',
          description: 'Izsekojiet pacientus, gultas, ratiņkrēslus un sūkņus reāllaikā.'
        },
        {
          title: 'Vides un Komforta Uzraudzība',
          description: 'CO₂, temperatūra, mitrums, miega modeļi.'
        },
        {
          title: 'Aprūpētāju un Medicīnas Platforma (eDoctor)',
          description: 'Brīdinājumi, piezīmes, tendences, uzturēšanas rīki — pieejami LV, LT, EE.'
        }
      ]
    },
    
    // Devices
    devices: {
      title: 'Mūsu Ierīces',
      subtitle: 'Medicīniskā Kvalitāte, Uzticamas, Vienkāršas',
      items: [
        'Aktīvu Izsekotājs',
        'Viedā Nozīme',
        'Signāla Pastiprinātājs',
        'Pacienta Aproce',
        'Gaisa Kvalitātes Sensors',
        'Panikas Poga',
        'Iekštelpu Pacientu Lokators',
        'Viedais Enerģijas Skaitītājs',
        'Citi Sensori'
      ]
    },
    
    // Outcomes
    outcomes: {
      title: 'Rezultāti un ROI',
      subtitle: 'Izmērāma ietekme uz izmaksām, aprūpes kvalitāti un darbības efektivitāti',
      items: [
        {
          title: 'Samazināt Izmaksas',
          bullets: [
            'Mazāk nevajadzīgu apmeklējumu',
            'Mazāk aprīkojuma zudumu',
            'Automatizēti žurnāli',
            'Mazāk ārkārtas gadījumu'
          ]
        },
        {
          title: 'Uzlabot Aprūpes Kvalitāti',
          bullets: [
            'Reāllaika brīdinājumi',
            'Agrīna risku atklāšana',
            'Pārredzamība ģimenēm',
            'Labāks komforts'
          ]
        },
        {
          title: 'Palielināt Personāla Efektivitāti',
          bullets: [
            'Vienoti informācijas paneļi',
            'WhatsApp brīdinājumi',
            'Balss palīdzība (Alexa vietējā)'
          ]
        }
      ]
    },
    
    // How It Works
    howItWorks: {
      title: 'Kā Tas Darbojas',
      subtitle: 'Vienkārša ieviešana, tūlītēja ietekme',
      steps: [
        'Uzstādiet sensorus un valkājamās ierīces minūtēs',
        'Vecāka gadagājuma cilvēki vai pacienti lieto pulksteni vai nozīmi',
        'Dati plūst uz eDoctor informācijas paneli',
        'Brīdinājumi caur WhatsApp vai Alexa',
        'Aprūpes komandas rīkojas nekavējoties'
      ],
      note: 'Nav nepieciešama IT integrācija.'
    },
    
    // Pilot Program
    pilot: {
      title: 'Sāciet Savu 30 Dienu Pilotu',
      subtitle: 'Izvēlieties savu pilota fokusu un redziet tūlītējus rezultātus',
      cards: [
        {
          title: 'Vecāka Gadagājuma Cilvēku Drošība un Mājas Uzraudzība',
          description: 'GPS pulkstenis, eBeat, PIR, ģimenes brīdinājumi.',
          button: 'Sākt Vecāka Gadagājuma Pilotu'
        },
        {
          title: 'Slimnīcas Izsekošana',
          description: 'Pacientu lokators, gultu izsekošana, aktīvu izsekošana.',
          button: 'Sākt Slimnīcas Pilotu'
        },
        {
          title: 'Vides Uzraudzība',
          description: 'CO₂, mitrums, temperatūra, miega analīze.',
          button: 'Sākt Vides Pilotu'
        },
        {
          title: 'Pilnīga Ekosistēma',
          description: 'Visas ierīces + informācijas paneļi + lietotnes.',
          button: 'Sākt Pilno Pilotu'
        }
      ],
      ctaPrimary: 'Sākt Pilotu Tagad',
      ctaSecondary: 'Rezervēt 10 Minūšu Zvanu'
    },
    
    // Contact Form
    contact: {
      title: 'Sazināties',
      subtitle: 'Pastāstiet mums par savām vajadzībām — mēs atbildēsim 24 stundu laikā',
      gdprNote: 'ES mitināts un VDAR saderīgs',
      gdprDescription: 'Jūsu dati ir drošībā un pilnībā atbilst Eiropas privātuma noteikumiem.',
      form: {
        fullName: 'Pilns Vārds',
        email: 'E-pasts',
        organisation: 'Organizācija',
        country: 'Valsts',
        phone: 'Tālrunis (neobligāts)',
        interests: 'Interešu Jomas',
        objectives: 'Mērķi',
        message: 'Ziņojums (neobligāts)',
        messagePlaceholder: 'Pastāstiet mums vairāk par savām vajadzībām...',
        submit: 'Nosūtīt Pieprasījumu',
        sending: 'Nosūta...'
      },
      interestOptions: [
        'Vecāka Gadagājuma Uzraudzība',
        'Pacientu Izsekošana',
        'Aprīkojuma Izsekošana',
        'Vides Uzraudzība',
        'Valkājamās Ierīces',
        'Komunikācijas Rīki',
        'Pilnīga Ekosistēma'
      ],
      objectiveOptions: [
        'Samazināt izmaksas',
        'Uzlabot aprūpes kvalitāti',
        'Palielināt drošību',
        'Palielināt personāla efektivitāti'
      ]
    },
    
    // Funding
    funding: {
      title: 'Finansējums un Budžeta Atbalsts',
      subtitle: 'slimnīcām un pašvaldībām',
      description: 'Mēs palīdzam jums nodrošināt nepieciešamo budžetu.',
      cards: [
        {
          title: 'Elastīgs Finansējums',
          items: [
            'OPEX mēneša plāni',
            'Aprīkojuma līzings (3-5 gadi)',
            'Bez sākotnējā CAPEX',
            'Viegli apstiprināms slimnīcām un pašvaldībām'
          ]
        },
        {
          title: 'ES un Valsts Finansējuma Saderība',
          items: [
            'Digitālā veselība',
            'Vecāka gadagājuma aprūpe / novecojošā populācija',
            'Viedās pilsētas iniciatīvas',
            'Sociālās aprūpes dotācijas',
            'ERAF / ESF+ / valsts programmas'
          ],
          note: 'Mēs varam nodrošināt pamatojuma paketi, lai atbalstītu jūsu finansējuma pieprasījumu.'
        },
        {
          title: 'Pilots kā Budžeta Atbloķēšanas Rīks',
          items: [
            '30 dienu pilots',
            'Reāli dati un ROI',
            'Palīdz pamatot iekšējo apstiprināšanu',
            'Bez saistībām'
          ]
        }
      ]
    },
    
    // FAQ
    faq: {
      title: 'Bieži Uzdotie Jautājumi',
      subtitle: 'Viss, kas jums jāzina par mūsu veselības ekosistēmu',
      items: [
        {
          question: 'Vai platforma ir VDAR saderīga?',
          answer: 'Jā — ES mitināta, privātuma aizsardzība pēc dizaina.'
        },
        {
          question: 'Vai lietotnes ir pieejamas LV, LT, EE?',
          answer: 'Jā — pilnīga lokalizācija vecāka gadagājuma cilvēkiem, aprūpētājiem un slimnīcām.'
        },
        {
          question: 'Cik ātri var notikt ieviešana?',
          answer: '24–48 stundas atkarībā no apjoma.'
        },
        {
          question: 'Vai atbalstāt gan vecāka gadagājuma cilvēkus mājās, gan slimnīcās?',
          answer: 'Jā — viena ekosistēma abiem.'
        },
        {
          question: 'Vai tiek atbalstīti WhatsApp brīdinājumi?',
          answer: 'Jā — tie nonāk tieši pie aprūpētājiem un ģimenes.'
        },
        {
          question: 'Vai pilots ir bez līguma?',
          answer: 'Jā — bez saistībām.'
        }
      ]
    },
    
    // Footer
    footer: {
      countries: {
        lithuania: 'Lietuva',
        latvia: 'Latvija',
        estonia: 'Igaunija'
      },
      copyright: 'Visas tiesības aizsargātas.'
    }
  },
  
  lt: {
    // Header
    nav: {
      solutions: 'Sprendimai',
      devices: 'Įrenginiai',
      pilot: 'Bandomoji Programa',
      contact: 'Kontaktai'
    },
    
    // Hero
    hero: {
      title: 'Išmanioji Sveikatos Ekosistema',
      titleHighlight: 'Senjorams ir Ligoninėms',
      benefits: ['Mažinti Išlaidas', 'Gerinti Saugumą', 'Remti Personalą'],
      description: 'Realaus laiko stebėjimas, pacientų sekimas, avariniai įspėjimai, aplinkos jutikliai ir slaugos įrankiai — visa viena platformoje. Programėlės prieinamos LV, LT, EE, EN.',
      ctaPrimary: 'Pradėti Bandomąją Programą',
      ctaSecondary: 'Tyrinėti Sprendimus'
    },
    
    // Challenges
    challenges: {
      title: 'Sveikatos Priežiūros Iššūkiai',
      subtitle: 'Šiuolaikinė sveikatos priežiūra susiduria su kritinėmis kliūtimis, kurioms reikia inovatyvių sprendimų',
      items: [
        {
          title: 'Augančios Veiklos Išlaidos',
          description: 'Personalas praranda laiką ieškodamas įrangos, atlikdamas nereikalingus apsilankymus ir tvarkydamas rankinius žurnalus.'
        },
        {
          title: 'Trūksta Realaus Laiko Matomumo',
          description: 'Vėluotas pavojų aptikimas, klaidžiojantys pacientai, valandų valandas nestebimi senjorai.'
        },
        {
          title: 'Personalo Perkrova',
          description: 'Per daug užduočių, per mažai žmonių. E2 automatizuoja ir supaprastina.'
        }
      ]
    },
    
    // Solutions
    solutions: {
      title: 'E2 Sveikatos Priežiūros Ekosistema',
      subtitle: 'Keturi integruoti sprendimai, skirti transformuoti seniorų priežiūrą ir ligoninių veiklą',
      learnMore: 'Sužinoti daugiau',
      items: [
        {
          title: 'Seniorų Sauga ir Namų Stebėjimas',
          description: 'Kritimų aptikimas, GPS sauga, elgesio analizė, šeimos įspėjimai, Alexa balso pagalba.'
        },
        {
          title: 'Ligoninės Pacientų ir Įrangos Sekimas',
          description: 'Sekite pacientus, lovas, neįgaliųjų vežimėlius ir siurblius realiuoju laiku.'
        },
        {
          title: 'Aplinkos ir Komforto Stebėjimas',
          description: 'CO₂, temperatūra, drėgmė, miego modeliai.'
        },
        {
          title: 'Slaugos ir Medicinos Platforma (eDoctor)',
          description: 'Įspėjimai, užrašai, tendencijos, stebėjimo įrankiai — prieinami LV, LT, EE.'
        }
      ]
    },
    
    // Devices
    devices: {
      title: 'Mūsų Įrenginiai',
      subtitle: 'Medicininės Klasės, Patikimi, Paprasti',
      items: [
        'Turto Sekiklis',
        'Išmanusis Ženklelis',
        'Signalo Stiprintuvas',
        'Paciento Apyrankė',
        'Oro Kokybės Jutiklis',
        'Panikos Mygtukas',
        'Vidaus Paciento Lokatorius',
        'Išmanusis Energijos Skaitiklis',
        'Kiti Jutikliai'
      ]
    },
    
    // Outcomes
    outcomes: {
      title: 'Rezultatai ir ROI',
      subtitle: 'Išmatuojamas poveikis išlaidoms, priežiūros kokybei ir veiklos efektyvumui',
      items: [
        {
          title: 'Mažinti Išlaidas',
          bullets: [
            'Mažiau nereikalingų apsilankymų',
            'Mažiau įrangos praradimų',
            'Automatizuoti žurnalai',
            'Mažiau avarių atvejų'
          ]
        },
        {
          title: 'Gerinti Priežiūros Kokybę',
          bullets: [
            'Realaus laiko įspėjimai',
            'Ankstyvasis pavojų aptikimas',
            'Skaidrumas šeimoms',
            'Geresnis komfortas'
          ]
        },
        {
          title: 'Didinti Personalo Efektyvumą',
          bullets: [
            'Suvienodinti skydeliai',
            'WhatsApp įspėjimai',
            'Balso pagalba (Alexa vietinė)'
          ]
        }
      ]
    },
    
    // How It Works
    howItWorks: {
      title: 'Kaip Tai Veikia',
      subtitle: 'Paprastas diegimas, momentinis poveikis',
      steps: [
        'Įdiekite jutiklius ir nešiojamus įrenginius per kelias minutes',
        'Senjorai ar pacientai naudoja laikrodį ar ženklelį',
        'Duomenys teka į eDoctor skydelį',
        'Įspėjimai per WhatsApp ar Alexa',
        'Priežiūros komandos veikia nedelsiant'
      ],
      note: 'Nereikia IT integracijos.'
    },
    
    // Pilot Program
    pilot: {
      title: 'Pradėkite Savo 30 Dienų Bandomąją Programą',
      subtitle: 'Pasirinkite savo bandomosios programos fokusą ir matykite nedelsiant rezultatus',
      cards: [
        {
          title: 'Seniorų Sauga ir Namų Stebėjimas',
          description: 'GPS laikrodis, eBeat, PIR, šeimos įspėjimai.',
          button: 'Pradėti Seniorų Bandomąją Programą'
        },
        {
          title: 'Ligoninės Sekimas',
          description: 'Paciento lokatorius, lovų sekimas, turto sekimas.',
          button: 'Pradėti Ligoninės Bandomąją Programą'
        },
        {
          title: 'Aplinkos Stebėjimas',
          description: 'CO₂, drėgmė, temperatūra, miego analizė.',
          button: 'Pradėti Aplinkos Bandomąją Programą'
        },
        {
          title: 'Visa Ekosistema',
          description: 'Visi įrenginiai + skydeliai + programėlės.',
          button: 'Pradėti Visą Bandomąją Programą'
        }
      ],
      ctaPrimary: 'Pradėti Bandomąją Programą Dabar',
      ctaSecondary: 'Užsakyti 10 Minučių Skambutį'
    },
    
    // Contact Form
    contact: {
      title: 'Susisiekite',
      subtitle: 'Papasakokite mums savo poreikius — atsakysime per 24 valandas',
      gdprNote: 'Talpinamas ES ir atitinka BDAR',
      gdprDescription: 'Jūsų duomenys yra saugūs ir visiškai atitinka Europos privatumo reglamentus.',
      form: {
        fullName: 'Pilnas Vardas',
        email: 'El. paštas',
        organisation: 'Organizacija',
        country: 'Šalis',
        phone: 'Telefonas (neprivaloma)',
        interests: 'Interesų Sritys',
        objectives: 'Tikslai',
        message: 'Žinutė (neprivaloma)',
        messagePlaceholder: 'Papasakokite mums daugiau apie savo poreikius...',
        submit: 'Siųsti Užklausą',
        sending: 'Siunčiama...'
      },
      interestOptions: [
        'Seniorų Stebėjimas',
        'Pacientų Sekimas',
        'Įrangos Sekimas',
        'Aplinkos Stebėjimas',
        'Nešiojami Įrenginiai',
        'Komunikacijos Įrankiai',
        'Visa Ekosistema'
      ],
      objectiveOptions: [
        'Mažinti išlaidas',
        'Gerinti priežiūros kokybę',
        'Didinti saugumą',
        'Didinti personalo efektyvumą'
      ]
    },
    
    // Funding
    funding: {
      title: 'Finansavimas ir Biudžeto Parama',
      subtitle: 'ligoninėms ir savivaldybėms',
      description: 'Padedame jums užsitikrinti reikiamą biudžetą.',
      cards: [
        {
          title: 'Lankstus Finansavimas',
          items: [
            'OPEX mėnesiniai planai',
            'Įrangos nuoma (3-5 metai)',
            'Nėra pradinio CAPEX',
            'Lengva patvirtinti ligoninėms ir savivaldybėms'
          ]
        },
        {
          title: 'ES ir Nacionalinio Finansavimo Suderinamumas',
          items: [
            'Skaitmeninė sveikata',
            'Seniorų priežiūra / senėjanti populiacija',
            'Išmaniųjų miestų iniciatyvos',
            'Socialinės priežiūros dotacijos',
            'ERPF / ESF+ / nacionalinės programos'
          ],
          note: 'Galime pateikti pagrindimo paketą, kad paremtume jūsų finansavimo prašymą.'
        },
        {
          title: 'Bandomoji Programa kaip Biudžeto Atrakinimo Įrankis',
          items: [
            '30 dienų bandomoji programa',
            'Tikri duomenys ir ROI',
            'Padeda pagrįsti vidinį patvirtinimą',
            'Be įsipareigojimų'
          ]
        }
      ]
    },
    
    // FAQ
    faq: {
      title: 'Dažnai Užduodami Klausimai',
      subtitle: 'Visa, ką reikia žinoti apie mūsų sveikatos ekosistemą',
      items: [
        {
          question: 'Ar platforma atitinka BDAR?',
          answer: 'Taip — talpinama ES, privatumas pagal dizainą.'
        },
        {
          question: 'Ar programėlės prieinamos LV, LT, EE?',
          answer: 'Taip — visa lokalizacija senjorų, slaugytojų ir ligoninių.'
        },
        {
          question: 'Kaip greitai gali įvykti diegimas?',
          answer: '24–48 valandas priklausomai nuo apimties.'
        },
        {
          question: 'Ar palaikote tiek senjorus namuose, tiek ligonines?',
          answer: 'Taip — viena ekosistema abiem.'
        },
        {
          question: 'Ar palaikomi WhatsApp įspėjimai?',
          answer: 'Taip — jie eina tiesiai slaugytojams ir šeimai.'
        },
        {
          question: 'Ar bandomoji programa be sutarties?',
          answer: 'Taip — be įsipareigojimų.'
        }
      ]
    },
    
    // Footer
    footer: {
      countries: {
        lithuania: 'Lietuva',
        latvia: 'Latvija',
        estonia: 'Estija'
      },
      copyright: 'Visos teisės saugomos.'
    }
  },
  
  ee: {
    // Header
    nav: {
      solutions: 'Lahendused',
      devices: 'Seadmed',
      pilot: 'Pilootprogramm',
      contact: 'Kontakt'
    },
    
    // Hero
    hero: {
      title: 'Nutikas Terviseökosüsteem',
      titleHighlight: 'Eakate Hoolduseks ja Haiglates',
      benefits: ['Vähenda Kulusid', 'Paranda Turvalisust', 'Toeta Personali'],
      description: 'Reaalajas jälgimine, patsientide jälgimine, hädahoiatused, keskkonnasensorid ja hooldajate tööriistad — kõik ühel EL-is majutatud platvormil. Rakendused saadaval LV, LT, EE, EN.',
      ctaPrimary: 'Alusta Pilooti',
      ctaSecondary: 'Uuri Lahendusi'
    },
    
    // Challenges
    challenges: {
      title: 'Tervishoiu Väljakutsed',
      subtitle: 'Kaasaegne tervishoid seisab silmitsi kriitiliste takistustega, mis nõuavad uuenduslikke lahendusi',
      items: [
        {
          title: 'Kasvavad Tegevuskulud',
          description: 'Personal kaotab aega varustuse otsimisel, mittevajalike visiitide tegemisel ja käsitsi logide haldamisel.'
        },
        {
          title: 'Reaalajas Nähtavuse Puudumine',
          description: 'Hiline riskide avastamine, uitlevad patsiendid, tundide kaupa jälgimata eakad.'
        },
        {
          title: 'Personali Ülekoormus',
          description: 'Liiga palju ülesandeid, liiga vähe inimesi. E2 automatiseerib ja lihtsustab.'
        }
      ]
    },
    
    // Solutions
    solutions: {
      title: 'E2 Terviseökosüsteem',
      subtitle: 'Neli integreeritud lahendust, mis on loodud eakate hoolduse ja haigla tegevuse muutmiseks',
      learnMore: 'Loe lähemalt',
      items: [
        {
          title: 'Eakate Turvalisus ja Kodujälgimine',
          description: 'Kukkumise tuvastamine, GPS turvalisus, käitumisanalüüsid, perehoiatused, Alexa häälabi.'
        },
        {
          title: 'Haigla Patsientide ja Varustuse Jälgimine',
          description: 'Jälgi patsiente, voodeid, ratastoolid ja pumpasid reaalajas.'
        },
        {
          title: 'Keskkonna ja Mugavuse Jälgimine',
          description: 'CO₂, temperatuur, niiskus, unemudelid.'
        },
        {
          title: 'Hooldajate ja Meditsiini Platvorm (eDoctor)',
          description: 'Hoiatused, märkmed, suundumused, järelkontrolli tööriistad — saadaval LV, LT, EE.'
        }
      ]
    },
    
    // Devices
    devices: {
      title: 'Meie Seadmed',
      subtitle: 'Meditsiinilise Kvaliteediga, Usaldusväärne, Lihtne',
      items: [
        'Vara Jälgija',
        'Nutikas Märk',
        'Signaali Võimendi',
        'Patsiendi Randmepael',
        'Õhukvaliteedi Sensor',
        'Paanika Nupp',
        'Sisepatsientide Lokaat',
        'Nutikas Energialugeja',
        'Muud Sensorid'
      ]
    },
    
    // Outcomes
    outcomes: {
      title: 'Tulemused ja ROI',
      subtitle: 'Mõõdetav mõju kuludele, hoolduse kvaliteedile ja tegevuse tõhususele',
      items: [
        {
          title: 'Vähenda Kulusid',
          bullets: [
            'Vähem mittevajalikke visiite',
            'Vähem varustuse kaotusi',
            'Automatiseeritud logid',
            'Vähem hädaolukordi'
          ]
        },
        {
          title: 'Paranda Hoolduse Kvaliteeti',
          bullets: [
            'Reaalajas hoiatused',
            'Varajane riskide tuvastamine',
            'Läbipaistvus peredele',
            'Parem mugavus'
          ]
        },
        {
          title: 'Suurenda Personali Tõhusust',
          bullets: [
            'Ühendatud armatuurlauad',
            'WhatsApp hoiatused',
            'Häälabi (Alexa kohalik)'
          ]
        }
      ]
    },
    
    // How It Works
    howItWorks: {
      title: 'Kuidas See Töötab',
      subtitle: 'Lihtne paigaldus, kohene mõju',
      steps: [
        'Paigalda sensorid ja kantavad seadmed minutitega',
        'Eakad või patsiendid kasutavad kella või märki',
        'Andmed voolavad eDoctor armatuurlauale',
        'Hoiatused WhatsAppi või Alexa kaudu',
        'Hooldusmeeskonnad tegutsevad kohe'
      ],
      note: 'IT integratsioon pole vajalik.'
    },
    
    // Pilot Program
    pilot: {
      title: 'Alusta Oma 30-päevast Pilooti',
      subtitle: 'Vali oma pil oodi fookus ja näe koheseid tulemusi',
      cards: [
        {
          title: 'Eakate Turvalisus ja Kodujälgimine',
          description: 'GPS kell, eBeat, PIR, perehoiatused.',
          button: 'Alusta Eakate Pilooti'
        },
        {
          title: 'Haigla Jälgimine',
          description: 'Patsiendi lokaator, voodite jälgimine, vara jälgimine.',
          button: 'Alusta Haigla Pilooti'
        },
        {
          title: 'Keskkonna Jälgimine',
          description: 'CO₂, niiskus, temperatuur, une analüüsid.',
          button: 'Alusta Keskkonna Pilooti'
        },
        {
          title: 'Täielik Ökosüsteem',
          description: 'Kõik seadmed + armatuurlauad + rakendused.',
          button: 'Alusta Täielikku Pilooti'
        }
      ],
      ctaPrimary: 'Alusta Pilooti Kohe',
      ctaSecondary: 'Broneeri 10-minutiline Kõne'
    },
    
    // Contact Form
    contact: {
      title: 'Võta Ühendust',
      subtitle: 'Räägi meile oma vajadustest — vastame 24 tunni jooksul',
      gdprNote: 'EL-is majutatud ja GDPR-iga ühilduv',
      gdprDescription: 'Teie andmed on turvalised ja täielikult vastavad Euroopa privaatsusseadustele.',
      form: {
        fullName: 'Täisnimi',
        email: 'E-post',
        organisation: 'Organisatsioon',
        country: 'Riik',
        phone: 'Telefon (valikuline)',
        interests: 'Huvivaldkonnad',
        objectives: 'Eesmärgid',
        message: 'Sõnum (valikuline)',
        messagePlaceholder: 'Räägi meile rohkem oma vajadustest...',
        submit: 'Saada Päring',
        sending: 'Saadan...'
      },
      interestOptions: [
        'Eakate Jälgimine',
        'Patsientide Jälgimine',
        'Varustuse Jälgimine',
        'Keskkonna Jälgimine',
        'Kantavad Seadmed',
        'Kommunikatsiooni Tööriistad',
        'Täielik Ökosüsteem'
      ],
      objectiveOptions: [
        'Vähenda kulusid',
        'Paranda hoolduse kvaliteeti',
        'Suurenda turvalisust',
        'Suurenda personali tõhusust'
      ]
    },
    
    // Funding
    funding: {
      title: 'Rahastamine ja Eelarve Tugi',
      subtitle: 'haiglates ja omavalitsustes',
      description: 'Aitame sul vajaliku eelarve tagada.',
      cards: [
        {
          title: 'Paindlik Rahastamine',
          items: [
            'OPEX kuuplaanid',
            'Varustuse liisimine (3-5 aastat)',
            'Ei esialgset CAPEX-i',
            'Lihtne kinnitada haiglates ja omavalitsustes'
          ]
        },
        {
          title: 'EL ja Riikliku Rahastamise Ühilduvus',
          items: [
            'Digitaalne tervis',
            'Eakate hooldus / vananev elanikkond',
            'Nutikate linnade algatused',
            'Sotsiaalhoolduse toetused',
            'ERF / ESF+ / riiklikud programmid'
          ],
          note: 'Saame pakkuda põhjenduspaketti, et toetada teie rahataotlust.'
        },
        {
          title: 'Pilot kui Eelarve Avamise Tööriist',
          items: [
            '30-päevane pilot',
            'Tegelikud andmed ja ROI',
            'Aitab põhjendada sisemist kinnitust',
            'Kohustuseta'
          ]
        }
      ]
    },
    
    // FAQ
    faq: {
      title: 'Korduma Kippuvad Küsimused',
      subtitle: 'Kõik, mida pead teadma meie terviseökosüsteemi kohta',
      items: [
        {
          question: 'Kas platvorm on GDPR-iga ühilduv?',
          answer: 'Jah — EL-is majutatud, privaatsus disaini järgi.'
        },
        {
          question: 'Kas rakendused on saadaval LV, LT, EE?',
          answer: 'Jah — täielik lokaliseerimine eakate, hooldajate ja haiglate jaoks.'
        },
        {
          question: 'Kui kiiresti saab paigaldus toimuda?',
          answer: '24–48 tundi olenevalt ulatusest.'
        },
        {
          question: 'Kas toetate nii eakaid kodus kui ka haiglates?',
          answer: 'Jah — üks ökosüsteem mõlemale.'
        },
        {
          question: 'Kas WhatsApp hoiatused on toetatud?',
          answer: 'Jah — nad lähevad otse hooldajatele ja perele.'
        },
        {
          question: 'Kas pilot on lepinguvaba?',
          answer: 'Jah — kohustuseta.'
        }
      ]
    },
    
    // Footer
    footer: {
      countries: {
        lithuania: 'Leedu',
        latvia: 'Läti',
        estonia: 'Eesti'
      },
      copyright: 'Kõik õigused kaitstud.'
    }
  }
};
